import { useState, useEffect } from 'react';
import { User, Mail, Phone, MapPin, Plus, Check, Trash2, X, ChevronDown, ShoppingBag, Package, Clock, CreditCard, ChevronRight } from 'lucide-react';
import './UserProfile.scss';
import { useAuth } from '../../context/AuthContext';
import { addAddressToUser, deleteAddressById, fetchUserAddresses, setAddressAsDefault } from '../../services/UsersApiService';
import { HttpStatusCode } from 'axios';
import { toast } from 'react-toastify';
import { getLanguage } from '../../utils/Helper';
import { Navigate } from 'react-router-dom';
import { egyptGovernorates, getGovernorateName } from '../../utils/Helper';
import OrderApiService from '../../services/OrderApiService';

const UserProfile = () => {
  const authContext = useAuth();
  const lang = getLanguage();
  
  const [userData, setUserData] = useState({
    id: '',
    email: '',
    mobile: '',
    firstName: '',
    lastName: ''
  });

  // Addresses state
  const [addresses, setAddresses] = useState([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);

  // Orders state
  const [orders, setOrders] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [ordersPage, setOrdersPage] = useState(0);
  const [hasMoreOrders, setHasMoreOrders] = useState(false);
  const [loadingMoreOrders, setLoadingMoreOrders] = useState(false);

  // Translations object
  const translations = {
    EN: {
      welcome: 'Welcome to your profile',
      personalInfo: 'Personal Information',
      emailAddress: 'Email Address',
      phoneNumber: 'Phone Number',
      fullName: 'Full Name',
      notProvided: 'Not provided',
      deliveryAddresses: 'Delivery Addresses',
      addAddress: 'Add Address',
      cancel: 'Cancel',
      streetAddress: 'Street Address',
      enterStreet: 'Enter street address',
      city: 'City',
      enterCity: 'Enter city',
      stateProvince: 'Governorate',
      selectGovernorate: 'Select governorate',
      enterPhone: 'Enter phone number',
      setAsDefault: 'Set as default address',
      saveAddress: 'Save Address',
      loadingAddresses: 'Loading addresses...',
      noAddresses: 'No addresses yet',
      addFirstAddress: 'Add your first delivery address to get started',
      default: 'Default',
      deleteConfirm: 'Are you sure you want to delete this address?',
      fillRequired: 'Please fill required fields',
      errorAdding: 'Error while adding address',
      governorateRequired: 'Please select a governorate',
      myOrders: 'My Orders',
      noOrders: 'No orders yet',
      startShopping: 'Start shopping to see your orders here',
      loadingOrders: 'Loading orders...',
      orderNumber: 'Order',
      placedOn: 'Placed on',
      total: 'Total',
      status: 'Status',
      paymentStatus: 'Payment',
      items: 'items',
      viewDetails: 'View Details',
      loadMore: 'Load More Orders',
      pickup: 'Pickup',
      delivery: 'Delivery',
      pending: 'Pending Review',
      confirmed: 'Confirmed',
      processing: 'Processing',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      paid: 'Paid',
      // unpaid: 'unpaid',
      partiallyPaid: 'Partially Paid',
      cancelled: 'Cancelled',
      refunded: 'Refunded',
      failed: 'Failed',
      withoutBox: 'Without Box'
    },
    AR: {
      welcome: 'مرحباً بك في صفحتك الشخصية',
      personalInfo: 'المعلومات الشخصية',
      emailAddress: 'البريد الإلكتروني',
      phoneNumber: 'رقم الهاتف',
      fullName: 'الاسم الكامل',
      notProvided: 'غير متوفر',
      deliveryAddresses: 'عناوين التوصيل',
      addAddress: 'إضافة عنوان',
      cancel: 'إلغاء',
      streetAddress: 'عنوان الشارع',
      enterStreet: 'أدخل عنوان الشارع',
      city: 'المدينة',
      enterCity: 'أدخل المدينة',
      stateProvince: 'المحافظة',
      selectGovernorate: 'اختر المحافظة',
      enterPhone: 'أدخل رقم الهاتف',
      setAsDefault: 'تعيين كعنوان افتراضي',
      saveAddress: 'حفظ العنوان',
      loadingAddresses: 'جاري تحميل العناوين...',
      noAddresses: 'لا توجد عناوين بعد',
      addFirstAddress: 'أضف أول عنوان توصيل للبدء',
      default: 'افتراضي',
      deleteConfirm: 'هل أنت متأكد من حذف هذا العنوان؟',
      fillRequired: 'برجاء إدخال بيانات العنوان الخاص بك كاملة',
      errorAdding: 'حدث خطأ أثناء إضافة العنوان برجاء التأكد من البيانات أو التجربة في وقت لاحق',
      governorateRequired: 'برجاء اختيار المحافظة',
      myOrders: 'طلباتي',
      noOrders: 'لا توجد طلبات بعد',
      startShopping: 'ابدأ التسوق لرؤية طلباتك هنا',
      loadingOrders: 'جاري تحميل الطلبات...',
      orderNumber: 'طلب رقم',
      placedOn: 'تم الطلب في',
      total: 'الإجمالي',
      status: 'الحالة',
      paymentStatus: 'الدفع',
      items: 'منتجات',
      viewDetails: 'عرض التفاصيل',
      loadMore: 'تحميل المزيد',
      pickup: 'استلام من الفرع',
      delivery: 'توصيل للمنزل',
      pending: 'قيد المراجعة',
      confirmed: 'مؤكد',
      processing: 'قيد التجهيز',
      shipped: 'تم الشحن',
      delivered: 'تم التوصيل',
      cancelled: 'ملغي',
      paid: 'مدفوع',
      // unpaid: 'معلق',
      partiallyPaid: 'جزء مدفوع',
      refunded: 'مسترجع',
      failed: 'فشل',
      withoutBox: 'بدون علبة'
    }
  };

  const t = translations[lang] || translations.EN;

  // New address form
  const [newAddress, setNewAddress] = useState({
    street: '',
    city: '',
    state: '',
    phoneNumber: '',
    isDefault: false
  });

  const governoratesList = egyptGovernorates.map(gov => ({
    id: gov.id,
    name: getGovernorateName(lang, gov)
  }));

  // Format currency
  const formatCurrency = (amount) => {
    if (typeof amount !== 'number') return lang === 'AR' ? '0.00 ج.م' : 'EGP 0.00';
    return lang === 'AR' 
      ? `${amount.toFixed(2)} ج.م`
      : `EGP ${amount.toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(lang === 'AR' ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status translation
  const getStatusText = (status) => {
    const statusMap = {
      'PENDING_REVIEW': t.pending,
      'CONFIRMED': t.confirmed,
      'PROCESSING': t.processing,
      'SHIPPED': t.shipped,
      'DELIVERED': t.delivered,
      'CANCELLED': t.cancelled
    };
    return statusMap[status] || status;
  };

  // Get payment status translation
  const getPaymentStatusText = (status) => {
    const statusMap = {
      'PARTIALLY_PAID': t.partiallyPaid,
      // 'PENDING': t.unpaid,
      'PAID': t.paid,
      'REFUNDED': t.refunded,
      'CANCELLED': t.cancelled,
      'FAILED': t.failed,
    };
    return statusMap[status] || status;
  };

  // Get status color
  const getStatusColor = (status) => {
    const colorMap = {
      'PENDING_REVIEW': '#f59e0b',
      'CONFIRMED': '#10b981',
      'PROCESSING': '#3b82f6',
      'SHIPPED': '#8b5cf6',
      'DELIVERED': '#059669',
      'CANCELLED': '#ef4444'
    };
    return colorMap[status] || '#6b7280';
  };

  const fetchAddresses = async () => {
    try {
      setIsLoadingAddresses(true);
      const userId = userData.id;
      
      const response = await fetchUserAddresses(userId);
      if (response.status === HttpStatusCode.Ok) {
        setAddresses(response.data);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  const fetchOrders = async (page = 0, append = false) => {
    try {
      if (page === 0) {
        setIsLoadingOrders(true);
      } else {
        setLoadingMoreOrders(true);
      }
      
      const response = await OrderApiService.getUserOrders(page, 5);
      
      if (append) {
        setOrders(prev => [...prev, ...response.orders]);
      } else {
        setOrders(response.orders);
      }
      
      setHasMoreOrders(response.hasMore);
      setOrdersPage(page);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error(lang === 'AR' 
        ? 'حدث خطأ أثناء تحميل الطلبات'
        : 'Error loading orders');
    } finally {
      setIsLoadingOrders(false);
      setLoadingMoreOrders(false);
    }
  };

  const handleLoadMoreOrders = () => {
    fetchOrders(ordersPage + 1, true);
  };

  const handleAddAddress = async () => {
    try {
      if(!newAddress.city || !newAddress.state || !newAddress.street ){
        toast.error(t.fillRequired);
        return;
      }
      
      if (!newAddress.state) {
        toast.error(t.governorateRequired);
        return;
      }
      
      const response = await addAddressToUser(newAddress);
      if (response.status === HttpStatusCode.Ok) {
        await fetchAddresses();
        setShowAddForm(false);
        setNewAddress({
          street: '',
          city: '',
          state: '',
          phoneNumber: '',
          isDefault: false
        });
      }
    } catch (error) {
      console.error('Error adding address:', error);
      toast.error(t.errorAdding);
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      const response = await setAddressAsDefault(addressId);
      if (response.status === HttpStatusCode.Ok) {
        await fetchAddresses();
      }
    } catch (error) {
      console.error('Error setting default address:', error);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (window.confirm(t.deleteConfirm)) {
      try {
        const response = await deleteAddressById(addressId);
        if (response.status === HttpStatusCode.Ok) {
          await fetchAddresses();
        }
      } catch (error) {
        console.error('Error deleting address:', error);
      }
    }
  };

  useEffect(() => {
    if (userData.id){
      fetchAddresses();
      fetchOrders();
    }
  }, [userData]);

  useEffect(() => {
    setUserData({
      id: authContext.user?.id,
      email: authContext.user?.email || '',
      mobile: authContext.user?.mobile || '',
      firstName: authContext.user?.firstName || '',
      lastName: authContext.user?.lastName || ''
    });
  }, [authContext?.user]);

  if (!authContext?.token) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <div className="user-profile-container">
      <div className="profile-glass-wrapper">
        {/* Header Section */}
        <div className="profile-header-glass">
          <div className="profile-avatar-blob">
            <div className="avatar-glass-card">
              <User size={48} />
            </div>
          </div>
          <div className="profile-header-info">
            <h1 className="profile-name">
              {userData.firstName} {userData.lastName}
            </h1>
            <p className="profile-subtitle">{t.welcome}</p>
          </div>
        </div>

        {/* User Info Card */}
        <div className="profile-section-glass">
          <div className="section-header">
            <h2 className="section-title">{t.personalInfo}</h2>
            <div className="title-underline"></div>
          </div>

          <div className="info-cards-grid">
            <div className="info-card-glass">
              <div className="info-icon-blob">
                <Mail size={20} />
              </div>
              <div className="info-content">
                <span className="info-label">{t.emailAddress}</span>
                <span className="info-value">{userData.email || t.notProvided}</span>
              </div>
            </div>

            <div className="info-card-glass">
              <div className="info-icon-blob">
                <Phone size={20} />
              </div>
              <div className="info-content">
                <span className="info-label">{t.phoneNumber}</span>
                <span className="info-value">{userData.mobile || t.notProvided}</span>
              </div>
            </div>

            <div className="info-card-glass">
              <div className="info-icon-blob">
                <User size={20} />
              </div>
              <div className="info-content">
                <span className="info-label">{t.fullName}</span>
                <span className="info-value">
                  {userData.firstName} {userData.lastName}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Addresses Section */}
        <div className="profile-section-glass">
          <div className="section-header">
            <div>
              <h2 className="section-title">{t.deliveryAddresses}</h2>
              <div className="title-underline"></div>
            </div>
            <button 
              className="add-address-btn"
              onClick={() => {setNewAddress(prev=> {return {...prev, phoneNumber: userData?.mobile}}); setShowAddForm(!showAddForm)}}
            >
              {showAddForm ? <X size={20} /> : <Plus size={20} />}
              <span>{showAddForm ? t.cancel : t.addAddress}</span>
            </button>
          </div>

          {showAddForm && (
            <div className="add-address-form-glass">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">{t.streetAddress}</label>
                  <input
                    type="text"
                    className="form-input-glass"
                    placeholder={t.enterStreet}
                    value={newAddress.street}
                    onChange={(e) => setNewAddress({ ...newAddress, street: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t.city}</label>
                  <input
                    type="text"
                    className="form-input-glass"
                    placeholder={t.enterCity}
                    value={newAddress.city}
                    onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t.stateProvince}</label>
                  <div className="select-wrapper-glass">
                    <select
                      className="form-input-glass select-input"
                      value={newAddress.state}
                      onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                    >
                      <option value="">{t.selectGovernorate}</option>
                      {governoratesList.map((gov) => (
                        <option key={gov.id} value={gov.name}>
                          {gov.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">{t.phoneNumber}</label>
                  <input
                    type="text"
                    className="form-input-glass"
                    placeholder={t.enterPhone}
                    value={newAddress.phoneNumber}
                    onChange={(e) => setNewAddress({ ...newAddress, phoneNumber: e.target.value })}
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label-glass">
                    <input
                      type="checkbox"
                      checked={newAddress.isDefault}
                      onChange={(e) => setNewAddress({ ...newAddress, isDefault: e.target.checked })}
                    />
                    <span className="checkbox-custom"></span>
                    <span>{t.setAsDefault}</span>
                  </label>
                </div>
              </div>

              <button className="save-address-btn" onClick={handleAddAddress}>
                <span>{t.saveAddress}</span>
              </button>
            </div>
          )}

          <div className="addresses-list">
            {isLoadingAddresses ? (
              <div className="loading-addresses">
                <div className="spinner-glass"></div>
                <p>{t.loadingAddresses}</p>
              </div>
            ) : addresses.length === 0 ? (
              <div className="empty-addresses">
                <MapPin size={48} />
                <h3>{t.noAddresses}</h3>
                <p>{t.addFirstAddress}</p>
              </div>
            ) : (
              addresses.map((address, index) => (
                <div 
                  key={index} 
                  className={`address-card-glass ${address.isDefault ? 'default' : ''}`}
                  style={{ '--index': index }}
                >
                  {address.isDefault && (
                    <div className="default-badge">
                      <Check size={14} />
                      <span>{t.default}</span>
                    </div>
                  )}

                  <div className="address-content">
                    <div className="address-icon-blob">
                      <MapPin size={24} />
                    </div>

                    <div className="address-details">
                      <h4 className="address-street">{address.street}</h4>
                      <p className="address-location">
                        {address.city}, {address.state}
                      </p>
                      {address.phoneNumber && <p className="address-phone">
                        <Phone size={14} />
                        {address.phoneNumber}
                      </p>}
                    </div>
                  </div>

                  <div className="address-actions">
                    {!address.isDefault && (
                      <button 
                        className="action-btn-glass set-default"
                        onClick={() => handleSetDefault(address.id)}
                        title={t.setAsDefault}
                      >
                        <Check size={18} />
                      </button>
                    )}
                    
                    <button 
                      className="action-btn-glass delete"
                      onClick={() => handleDeleteAddress(address.id)}
                      title={t.deleteConfirm}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Orders Section */}
        <div className="profile-section-glass">
          <div className="section-header">
            <div>
              <h2 className="section-title">{t.myOrders}</h2>
              <div className="title-underline"></div>
            </div>
          </div>

          <div className="orders-list">
            {isLoadingOrders ? (
              <div className="loading-orders">
                <div className="spinner-glass"></div>
                <p>{t.loadingOrders}</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="empty-orders">
                <ShoppingBag size={48} />
                <h3>{t.noOrders}</h3>
                <p>{t.startShopping}</p>
              </div>
            ) : (
              <>
                {orders.map((order, index) => (
                  <div 
                    key={order.id} 
                    className="order-card-glass"
                    style={{ '--index': index }}
                  >
                    <div className="order-header">
                      <div className="order-number-section">
                        <Package size={20} />
                        <div>
                          <span className="order-label">{t.orderNumber}</span>
                          <span className="order-number">{order.orderNumber}</span>
                        </div>
                      </div>
                      <div className="order-date">
                        <Clock size={16} />
                        <span>{formatDate(order.placedAt)}</span>
                      </div>
                    </div>

                    <div className="order-info-row">
                      <div className="order-info-item">
                        <span className="order-info-label">{t.status}</span>
                        <span 
                          className="order-status-badge"
                          style={{ backgroundColor: getStatusColor(order.status) }}
                        >
                          {getStatusText(order.status)}
                        </span>
                      </div>

                      <div className="order-info-item">
                        <span className="order-info-label">{t.paymentStatus}</span>
                        <span className={`payment-status ${order.paymentStatus === 'PAID' ? 'paid' : 'pending'}`}>
                          {getPaymentStatusText(order.paymentStatus)}
                        </span>
                      </div>

                      <div className="order-info-item">
                        <span className="order-info-label">{t.total}</span>
                        <span className="order-total">{formatCurrency(order.totalAmount)}</span>
                      </div>
                    </div>

                    {order.pickFromStore && (
                      <div className="order-pickup-badge">
                        <MapPin size={14} />
                        <span>{t.pickup}</span>
                      </div>
                    )}

                    {/* Order Items Images */}
                    <div className="order-items-preview">
                      <div className="items-scroll-container">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="order-item-image-wrapper">
                            <img 
                              src={item.imageUrl} 
                              alt={item.productName}
                              className="order-item-image"
                            />
                            {item.quantity > 1 && (
                              <span className="item-quantity-badge">
                                ×{item.quantity}
                              </span>
                            )}
                            
                            {/* Without Box Badge */}
                            {item.withoutBox && (
                              <span className="without-box-badge">
                                📦 {t.withoutBox}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="items-count">
                        {order.items.length} {t.items}
                      </div>
                    </div>

                    <div className="order-actions">
                      <button className="view-order-details-btn">
                        <span>{t.viewDetails}</span>
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                ))}

                {hasMoreOrders && (
                  <button 
                    className="load-more-orders-btn"
                    onClick={handleLoadMoreOrders}
                    disabled={loadingMoreOrders}
                  >
                    {loadingMoreOrders ? (
                      <>
                        <div className="spinner-glass small"></div>
                        <span>{t.loadingOrders}</span>
                      </>
                    ) : (
                      <span>{t.loadMore}</span>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default UserProfile;